import React, { useEffect, useState } from 'react';
import { MsalProvider as MsalReactProvider } from '@azure/msal-react';
import { EventType, EventMessage, AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { msalInstance } from '@/config/msal';
import { useAuthStore } from '@/stores/authStore';

interface MsalProviderProps {
  children: React.ReactNode;
}

// Helper to extract user info from MSAL account
function extractUserFromAccount(account: AccountInfo) {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const name = account.name || (claims?.name as string) || '';
  const email = (claims?.email as string) || (claims?.preferred_username as string) || account.username || '';

  // Detect B2B guest users by checking for #EXT# or mail# patterns in username
  const username = account.username || '';
  const isGuest = username.includes('#EXT#') || username.startsWith('mail#');

  return {
    id: account.localAccountId,
    name,
    email,
    role: 'staff' as const,
    isGuest,
  };
}

export default function MsalProvider({ children }: MsalProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const setUser = useAuthStore((state) => state.setUser);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        await msalInstance.initialize();

        // Handle redirect promise
        const response = await msalInstance.handleRedirectPromise();

        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
          setUser(extractUserFromAccount(response.account));
        } else {
          // Check for existing accounts
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
            setUser(extractUserFromAccount(accounts[0]));
          }
        }
      } catch (error) {
        console.error('MSAL initialization error:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeMsal();

    // Listen for login events
    const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        if (account) {
          msalInstance.setActiveAccount(account);
          setUser(extractUserFromAccount(account));
        }
      }

      if (event.eventType === EventType.LOGOUT_SUCCESS) {
        setUser(null);
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, [setUser]);

  if (!isInitialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#F9FAFB'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40,
            height: 40,
            border: '3px solid #E5E7EB',
            borderTopColor: '#0F6E56',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#6B7280', margin: 0 }}>Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <MsalReactProvider instance={msalInstance}>
      {children}
    </MsalReactProvider>
  );
}
